/**
 * Phase 21 — the Drop slots list: every drop of the day in time order, all
 * groups together. Drag a drop on the clock to move it (the drops after it
 * shift one tick); a pinned drop stays at its minute. One amount per drop;
 * a group's own amount lives in the drop's group picker.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { dragEndToMove, useBinomDragSensors } from '@/features/flow-optimizer/tabs/standard/binom/workspace/list/use-binom-drag-sensors';

import type { SlotTime } from '../../../../../utils/slot-times';
import { useDayPreview } from '../../../../../useDropGenerator';
import { useCountryClock } from '../../../../shared/time/country-clock-context';
import type { ContainerEditor } from '../../../state/useContainerEditor';
import type { GroupRef } from '../../hooks/use-general-times';
import { DROP_GRID, dropRows } from './drop-list-order';
import { DropRow } from './DropRow';
import { previewOf } from './cells/preview-of';
import { gatewayAutoWords } from './slot-gateway-labels';
import { type CapCuts, capMarkOf, NO_CUTS } from '../../../../shared/cap/cap-cuts';

interface Props {
  editor: ContainerEditor;
  countryKey?: string;
  containerId?: string | null;
  timesByKey: Map<string, SlotTime>;
  groupRefs: GroupRef[];
  /** 20/10/2026 — the slots the daily SMS cap cut or clamped today. */
  cuts?: CapCuts;
}

// Opaque (the same tint, mixed over the page) and above the rows — they scroll under it, never show through.
const HEAD = `${DROP_GRID} sticky top-0 z-10 border-b border-border/60 bg-[color-mix(in_srgb,hsl(var(--muted))_40%,hsl(var(--background)))] px-2 py-1.5 text-[11px] font-medium text-muted-foreground`;

export const DropList: FC<Props> = ({ editor, countryKey, timesByKey, groupRefs, containerId = null, cuts = NO_CUTS }) => {
  const { flag } = useCountryClock(); // Danish first, the country's time in the next column (15/10/2026)
  const { draft, slotMove, slotPatch, slotRemove } = editor;
  const preview = useDayPreview(countryKey ?? null);
  const plan = previewOf(preview.data?.containers ?? [], draft.slots, containerId);
  const plannedOf = (id: string | null) => (id === null ? [] : plan.drops.filter((d) => d.slotId === id || d.slotRowId === id));
  const gatewayWords = gatewayAutoWords(draft.gateway.strategy, draft.kind === 'ROUTE_TEST');
  const sensors = useBinomDragSensors();
  const rows = dropRows(draft.slots, timesByKey);
  const dragIds = rows.filter((r) => !r.pinned).map((r) => r.key);
  const indexOf = new Map(rows.map((r) => [r.key, r.index]));
  const onDragEnd = (event: DragEndEvent) => {
    const move = dragEndToMove(event, dragIds);
    if (!move) return;
    slotMove(indexOf.get(dragIds[move.from]) as number, indexOf.get(dragIds[move.to]) as number);
  };
  if (rows.length === 0)
    return (
      <p className="text-sm text-muted-foreground">
        No drops yet — each row is one drop of the day: when, to which groups, how many SMS. Amounts left empty use the default (
        {draft.defaultAmount.toLocaleString('en-US')}).
      </p>
    );
  return (
    <div className="max-h-[480px] overflow-y-auto overflow-x-hidden rounded-lg border border-border/60">
      <div className={HEAD}>
        <span />
        <span>#</span>
        <span>Time 🇩🇰 · {flag}</span>
        <span>Group · list</span>
        <span className="truncate" title={`Unpinned drops follow the gateway strategy: ${gatewayWords.caption}`}>
          Gateway · {gatewayWords.name.toLowerCase()}
        </span>
        <span>Amount</span>
        <span>On</span>
        <span />
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={dragIds} strategy={verticalListSortingStrategy}>
          {rows.map((row, i) => (
            <DropRow
              key={row.key}
              row={row}
              position={i + 1}
              groupRefs={groupRefs}
              planned={plannedOf(row.slot.id)}
              planGroups={plan.groups}
              defaultAmount={draft.defaultAmount}
              gateway={draft.gateway}
              routeTest={draft.kind === 'ROUTE_TEST'}
              countryKey={countryKey}
              onPatch={(patch) => slotPatch(row.index, patch)}
              onRemove={() => slotRemove(row.index)}
              cap={capMarkOf(cuts, row.slot.id, plannedOf(row.slot.id))}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};
